<template>
  <router-link
    :to="{ name: 'UserAccountEdit', params: { userId: userAccount.user?.id } }"
    class="team-cell-content team-link break-all"
  >
    {{ userAccount.user?.name }}
  </router-link>
  <div class="team-cell-content break-all">
    {{ userAccount.user?.email }}
  </div>
  <div class="team-cell-content">
    {{ userAccount.teamRole?.name }}
  </div>
  <div class="team-cell-content font-xl ml-3">
    <div v-if="userAccount.user?.active">
      <iconify-icon
        icon="mdi:check-bold"
        class="text-vp-secondary-3"
      />
    </div>
    <div
      v-else
      class="flex items-center text-vp-primary"
    >
      <VPButton
        variant="text"
        class="p-0"
        :disabled="isDisabled || isActive"
        @click.stop.prevent="resendInvite(userAccount.id)"
      >
        <iconify-icon
          icon="mdi:reload"
          class="mr-1"
          :class="{'animate-spin' : isActive}"
        />
        {{ $gettext('Resend Invite') }}
      </VPButton>
    </div>
  </div>
  <div class="team-cell-content p-0">
    <VPButton
      class="float-right"
      type="button"
      variant="text"
      @click.stop.prevent="emit('delete', userAccount)"
    >
      <iconify-icon
        icon="mdi:trash-can-outline"
        class="ml-auto mr-auto text-gray-900 ml-3 hover:text-vp-primary"
      />
    </VPButton>
  </div>
  <div class="col-span-5 border-b pb" />
</template>

<script setup lang='ts'>
import VPButton from '@/components/form/VPButton.vue';
import { useUserAccountsStore } from '@/stores/user-accounts';
import { type UserAccount } from '@/types/UserAccount';
import { ref } from 'vue';
import { useGettext } from 'vue3-gettext';
import * as Sentry from '@sentry/vue';
import { useToast } from 'vue-toastification';

type Props = {
  userAccount: UserAccount;
};

defineProps<Props>();

const emit = defineEmits<{
  (e: 'delete', userAccount: UserAccount): void;
}>();

const toast = useToast();
const userStore = useUserAccountsStore();
const { $gettext } = useGettext();
const isDisabled = ref(false);
const isActive = ref(false);

const resendInvite = async (userAccountId: number) => {
  isActive.value = true;
  try {
    const { errors } = await userStore.resendInvite(userAccountId);

    if (errors) {
      for (const error of errors) {
        toast.error(error);
      }
      return;
    }
    isDisabled.value = true;
    toast.success($gettext('Invitation has been resent'));
  } catch (e) {
    Sentry.captureException(e, {
      extra: {
        context: 'Error while resending invite',
      },
    });
    toast.error($gettext('Something went wrong. Please try again or contact support'));
  } finally {
    isActive.value = false;
  }
};
</script>
